/**
 * Card carousel behaviour:
 *  1. Positions the initial scroll so the configured card starts centered
 *     (the design presents the carousel mid-scroll with neighbours peeking).
 *  2. Lazy video playback — a `[data-video-lazy]` video downloads and plays
 *     (muted, looping) only while its card is at least half visible, and
 *     pauses otherwise. This keeps three reels off the page's initial load
 *     and only ever plays the card(s) the viewer is actually looking at.
 *     Honors prefers-reduced-motion by leaving the poster frame static.
 *  3. The player — opening a card shows its clip full screen, with sound,
 *     the product underneath and the rest of the row a step away. The row's
 *     own cards stay muted wallpaper; this is where a video is watched.
 *
 * The product page's Watch and Buy row is this element too, without a track
 * or rotation: there it only plays the clips on screen and runs the player,
 * so a clip opens the same way on both pages.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Read straight off <html>, so this never depends on oracura-motion.js loading first. */
const motionPaused = () => document.documentElement.dataset.oracuraMotion === 'paused';

/** Marks the history entry the open player stands on. */
const HISTORY_KEY = 'oracuraVideoViewer';

class OracuraVideoCards extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-video-cards-track]');
    // Center the initial card only in the mobile center-peek layout. The desktop
    // layout is a left-aligned row that should start at the first card, so
    // centering there would push the row rightward and open an empty left band.
    const isPeek = window.matchMedia('(max-width: 749px)').matches;
    if (track && isPeek) {
      const index = Number(this.getAttribute('initial-card') || 1) - 1;
      const card = track.children[index];
      if (card) {
        track.scrollLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
      }
    }

    this.setupLazyVideo();
    this.setupAutoScroll(track);
    this.setupViewer();
  }

  /**
   * The player.
   *
   * Nothing is built ahead of time: opening a card creates a video from that
   * card's own <source> elements, and closing throws it away. A second copy
   * of five reels sitting in the page would cost what the lazy row above
   * exists to avoid.
   *
   * Sound is the point of opening one, so the clip starts unmuted. A browser
   * may still refuse to play it that way; if it does, it falls back to muted
   * rather than to a still frame, and the control says what it would do.
   *
   * Only the cards in this element are stepped through, so a clip the row
   * left out (Watch and Buy drops hidden and excluded clips before drawing
   * anything) is never one swipe away in here either.
   */
  setupViewer() {
    this.viewer = this.querySelector('[data-video-viewer]');
    if (!this.viewer || typeof this.viewer.showModal !== 'function') return;

    this.stage = this.viewer.querySelector('[data-viewer-stage]');
    this.openable = [...this.querySelectorAll('[data-video-card]')].filter((card) => card.querySelector('video'));
    if (!this.stage || !this.openable.length) return;

    this.wantsSound = true;
    this.historyEntry = false;
    this.leaving = false;

    // Tied to this connection, so an element the page moves (or the theme
    // editor re-renders) does not end up answering every tap twice.
    this.viewerLife?.abort();
    this.viewerLife = new AbortController();
    const { signal } = this.viewerLife;

    this.addEventListener(
      'click',
      (event) => {
        const opener = event.target.closest('[data-video-open]');
        if (!opener) return;
        event.preventDefault();
        this.openViewer(this.openable.indexOf(opener.closest('[data-video-card]')));
      },
      { signal }
    );

    this.viewer.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-viewer-close]')) {
          this.viewer.close();
          return;
        }
        const step = event.target.closest('[data-viewer-step]');
        if (step) {
          this.step(Number(step.dataset.viewerStep));
          return;
        }
        const sound = event.target.closest('[data-viewer-sound]');
        if (sound) {
          this.toggleSound();
          return;
        }
        const pick = event.target.closest('[data-viewer-pick]');
        if (pick) {
          this.pick(pick);
          return;
        }
        const submit = event.target.closest('[data-viewer-submit]');
        if (submit) {
          this.submitForm(submit);
          return;
        }
        // The product in the strip, followed from the player: the page it
        // opens takes the player's place in the history, so Back from there
        // lands on this page rather than on a player that is no longer open.
        // A click meant for a new tab or window is left to the browser.
        const link = event.target.closest('a[href]');
        if (link) {
          const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
          if (plain && (!link.target || link.target === '_self') && this.historyEntry && history.state?.[HISTORY_KEY]) {
            event.preventDefault();
            this.historyEntry = false;
            location.replace(link.href);
          }
          return;
        }
        // Anywhere off the clip and its product is the way out, as it is in
        // every other lightbox. The stage fills the screen, so asking whether
        // the click was off the stage never let anything close it.
        if (!event.target.closest('.oracura-video-cards__viewer-video, .oracura-video-cards__viewer-strip, button, a')) {
          this.viewer.close();
        }
      },
      { signal }
    );

    this.viewer.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      },
      { signal }
    );

    // A swipe is how this is used on a phone, so the arrows are not the only
    // way through the row.
    let startX = null;
    this.viewer.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches.length === 1 ? event.touches[0].clientX : null;
      },
      { passive: true, signal }
    );
    this.viewer.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
      },
      { passive: true, signal }
    );

    // However the player is dismissed (the close button, the backdrop,
    // Escape, the back gesture) the dialog fires this.
    this.viewer.addEventListener(
      'close',
      () => {
        this.viewerClosed();
        this.returnToCard();
        // Anything waiting on the player being out of the way (the product
        // form, submitted from its Add to cart) goes now, not before.
        const next = this.afterClose;
        this.afterClose = null;
        next?.();
      },
      { signal }
    );

    // Back closes the player rather than leaving the page. A full-screen clip
    // reads as a page of its own, and on a phone the back gesture is how a
    // page is left: without an entry of its own, swiping back from the clip
    // took the shopper off the product they were watching. Capture, so this
    // runs before the page's own popstate handlers: the step is the player's
    // alone and the product page must not re-read its variant on it.
    window.addEventListener('popstate', (event) => this.onHistory(event), { capture: true, signal });
  }

  openViewer(index) {
    if (index < 0) return;
    this.index = index;
    this.afterClose = null;
    this.renderViewer();
    if (!this.viewer.open) {
      this.viewer.showModal();
      // showModal blocks interaction with the page but not scrolling of it,
      // and a page moving behind a full-screen player reads as a fault. The
      // theme's own lock: it covers the desktop layout, where the page scrolls
      // inside .page-wrapper rather than on <html>, and it lets go by itself
      // if the player is taken out of the page while open.
      lockScroll(this.viewer);
      // Still stepping back off the last entry: that step pushes this one.
      if (!this.leaving) this.pushEntry();
      // The row goes still behind the clip being watched.
      this.syncVideos?.();
    }
  }

  /**
   * The sound button sits on the clip's own corner. The frame it is placed in
   * is as wide as the stage, while a portrait reel takes only a slice of that
   * width, so a corner of the frame put the button far off to the side of the
   * picture on a desktop. The clip's box is the picture (it keeps its own
   * shape), so the button is measured onto it whenever either moves.
   */
  placeSound(frame, video, sound) {
    const place = () => {
      if (!frame.isConnected) return;
      const f = frame.getBoundingClientRect();
      const v = video.getBoundingClientRect();
      if (!v.width || !v.height) return;
      sound.style.top = `${Math.round(v.top - f.top + 10)}px`;
      sound.style.insetInlineEnd = `${Math.round(f.right - v.right + 10)}px`;
    };
    video.addEventListener('loadedmetadata', place);
    video.addEventListener('loadeddata', place);
    if (typeof ResizeObserver !== 'undefined') {
      this.soundObserver?.disconnect();
      this.soundObserver = new ResizeObserver(place);
      this.soundObserver.observe(frame);
      this.soundObserver.observe(video);
    }
    place();
  }

  /** Everything opening the player took, given back. Safe to run twice. */
  viewerClosed() {
    this.soundObserver?.disconnect();
    this.soundObserver = null;
    this.stage.replaceChildren();
    this.current = null;
    unlockScroll(this.viewer);
    if (this.historyEntry) {
      this.historyEntry = false;
      // Closed by the button, the backdrop or Escape: take back the entry the
      // player added, so the next Back leaves the page as it should.
      if (history.state?.[HISTORY_KEY]) {
        this.leaving = true;
        history.back();
      }
    }
  }

  pushEntry() {
    try {
      const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
      history.pushState({ ...state, [HISTORY_KEY]: true }, '');
      this.historyEntry = true;
    } catch {
      // A sandboxed frame can refuse. The close button and Escape still work.
    }
  }

  /** Back on the page's own entry: its address names the variant chosen in the player. */
  keepChosenUrl() {
    if (!this.chosenUrl) return;
    try {
      history.replaceState(history.state, '', this.chosenUrl);
    } catch {
      /* The page still shows the choice; only its address is behind. */
    }
    this.chosenUrl = null;
  }

  onHistory(event) {
    if (this.leaving) {
      // The step back the player took for itself on closing.
      this.leaving = false;
      event.stopImmediatePropagation();
      this.keepChosenUrl();
      // Opened again before that step landed: it needs its entry back.
      if (this.viewer.open) this.pushEntry();
      return;
    }
    if (!this.historyEntry || !this.viewer.open) return;
    // Back, with the player open. That is a close, not a change of page.
    this.historyEntry = false;
    event.stopImmediatePropagation();
    this.keepChosenUrl();
    this.viewer.close();
  }

  /** Back to where it was left, with focus on the clip last watched. */
  returnToCard() {
    const card = this.openable?.[this.index];
    if (!card?.isConnected) return;
    // Instant: the reader's place in the row, not a ride back to it.
    card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
    card.querySelector('[data-video-open]')?.focus({ preventScroll: true });
  }

  step(by) {
    if (!Number.isFinite(by) || !this.openable || this.openable.length < 2) return;
    this.index = (this.index + by + this.openable.length) % this.openable.length;
    this.renderViewer();
  }

  toggleSound() {
    this.wantsSound = !this.wantsSound;
    if (this.current) this.current.muted = !this.wantsSound;
    this.markSound();
  }

  markSound() {
    const button = this.viewer.querySelector('[data-viewer-sound]');
    if (!button || !this.current) return;
    const on = !this.current.muted;
    button.innerHTML = on
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" stroke="none"/><path d="M17 8.5a5 5 0 0 1 0 7"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" stroke="none"/><path d="m16 9 5 6m0-6-5 6"/></svg>';
    const label = on ? this.viewer.dataset.soundOffLabel : this.viewer.dataset.soundOnLabel;
    button.setAttribute('aria-label', label || '');
  }

  /**
   * What the Add to cart under a clip sells. A card names its variant, and
   * says when there is none left to sell. A card on a product page names
   * that page's product form instead, so the button sells whatever the
   * shopper has chosen there, at that choice's price, rather than the variant
   * the page happened to load with.
   */
  saleFor(card) {
    const form = card.dataset.viewerForm ? document.getElementById(card.dataset.viewerForm) : null;
    const field = form?.elements?.namedItem('id');
    if (field && 'value' in field) {
      const option = field.selectedOptions?.[0];
      return {
        id: field.value,
        // Not form.id: the form's own "id" field answers to that name.
        form: card.dataset.viewerForm,
        available: Boolean(field.value) && !option?.disabled,
        price: option?.dataset.viewerPrice,
        compare: option?.dataset.viewerCompare,
      };
    }
    const id = card.dataset.viewerVariant;
    if (!id) return null;
    // A card that carries its product's choices has each one's price too.
    const variant = this.cardOptions(card)?.variants?.find((candidate) => String(candidate.id) === id);
    return { id, available: !card.hasAttribute('data-viewer-sold-out'), price: variant?.price, compare: variant?.compare };
  }

  /** The choices a card carries for its product (snippets/oracura-video-card-options), read once. */
  cardOptions(card) {
    if (!('viewerOptions' in card)) {
      try {
        card.viewerOptions = JSON.parse(card.querySelector('script[data-viewer-options]')?.textContent || 'null');
      } catch {
        card.viewerOptions = null;
      }
    }
    return card.viewerOptions;
  }

  /**
   * The model and colour Add to cart sells, offered right under the clip. A
   * clip can show a black flosser or a peach one while the page (or the card)
   * stands on its opening white or green, and the button added that: the
   * strip named no colour and asked for none, and customers who had picked
   * the colour they saw in the video were sent the other one.
   *
   * On the clip's own product page these are the page's pickers: choosing
   * here chooses there, so the page's price, gallery, link and everything
   * its Add to Bag does stay in step. Anywhere else the card's own list of
   * variants says what each choice comes to.
   *
   * Each group is { name, colour, values: [{ value, text, on, soldOut, pick }] };
   * a choice the product only has one of is left out. The colour comes first,
   * as on the product page: it is what the clip shows.
   */
  choicesFor(card) {
    return this.optionGroups(card).sort((a, b) => Number(b.colour) - Number(a.colour));
  }

  optionGroups(card) {
    const form = card.dataset.viewerForm ? document.getElementById(card.dataset.viewerForm) : null;
    if (form) {
      const root = form.closest('oracura-product') || document;
      return [...root.querySelectorAll('[data-option-index]')]
        .map((group) => {
          const values = [...group.querySelectorAll('input[data-option-value]')]
            .filter((input) => !input.closest('label')?.hidden)
            .map((input) => {
              const label = input.closest('label');
              const text = label?.querySelector('.oracura-pdp__colour-label, .oracura-pdp__model-title')?.textContent || input.value;
              return {
                value: input.value,
                text: text.replace(/\s+/g, ' ').trim(),
                on: input.checked,
                soldOut: Boolean(label?.classList.contains('is-unavailable')),
                pick: () => {
                  input.checked = true;
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                },
              };
            });
          return {
            name: group.querySelector('legend')?.textContent.trim() || '',
            colour: group.matches('.oracura-pdp__colours'),
            values,
          };
        })
        .filter((group) => group.values.length > 1);
    }

    const data = this.cardOptions(card);
    if (!data?.variants?.length) return [];
    const current = data.variants.find((variant) => String(variant.id) === card.dataset.viewerVariant) || data.variants[0];
    return data.options
      .map((name, at) => {
        const colour = /colou?r/i.test(name);
        const values = [...new Set(data.variants.map((variant) => variant.options[at]))];
        return {
          name,
          colour,
          values: values.map((value) => {
            // With the other choices as they stand, is there one of this to sell?
            const fits = data.variants.filter(
              (variant) => variant.options[at] === value && variant.options.every((option, i) => i === at || option === current.options[i])
            );
            return {
              value,
              // "Lite (2 Nozzle Tips)" reads as Lite here; the colour as it is.
              text: colour ? value : value.replace(/\s*\(.*\)\s*$/, ''),
              on: current.options[at] === value,
              soldOut: !fits.some((variant) => variant.available),
              pick: () => {
                const chosen = current.options.slice();
                chosen[at] = value;
                // As on the product page: the choice just made stands, and the
                // rest moves to the closest variant that has it, in stock first.
                const kept = (variant) => variant.options.filter((option, i) => option === chosen[i]).length;
                const next =
                  data.variants.find((variant) => variant.options.every((option, i) => option === chosen[i])) ||
                  data.variants
                    .filter((variant) => variant.options[at] === value)
                    .reduce((best, variant) =>
                      !best || (variant.available && !best.available) || (variant.available === best.available && kept(variant) > kept(best)) ? variant : best,
                    null);
                if (!next) return;
                card.dataset.viewerVariant = next.id;
                card.toggleAttribute('data-viewer-sold-out', !next.available);
              },
            };
          }),
        };
      })
      .filter((group) => group.values.length > 1);
  }

  /** A choice tapped in the strip: make it, then redraw the strip (the clip plays on). */
  pick(button) {
    const card = this.openable[this.index];
    if (!card) return;
    const group = this.choicesFor(card)[Number(button.dataset.group)];
    const choice = group?.values.find((value) => value.value === button.dataset.value);
    if (!choice || choice.on) return;
    choice.pick();
    // The product page writes its new ?variant= over the history entry the
    // player stands on, and its mark with it: put the mark back, and keep the
    // address to restore to the page's own entry when the player goes.
    if (this.historyEntry) {
      this.chosenUrl = location.href;
      try {
        if (!history.state?.[HISTORY_KEY]) history.replaceState({ ...(history.state || {}), [HISTORY_KEY]: true }, '', location.href);
      } catch {
        /* Closing still works; Back may need one more press. */
      }
    }
    const refocus = document.activeElement === button ? { group: button.dataset.group, value: button.dataset.value } : null;
    const strip = this.stage.querySelector('.oracura-video-cards__viewer-strip');
    const next = this.buildStrip(card);
    if (!strip || !next) return;
    strip.replaceWith(next);
    if (refocus) {
      [...next.querySelectorAll('[data-viewer-pick]')]
        .find((chip) => chip.dataset.group === refocus.group && chip.dataset.value === refocus.value)
        ?.focus();
    }
  }

  /** The choices as rows of chips: LITE / PRO, then the colours with a dot of each. */
  choiceChips(groups) {
    const box = document.createElement('div');
    box.className = 'oracura-video-cards__viewer-choices';
    groups.forEach((group, g) => {
      const row = document.createElement('div');
      row.className = 'oracura-video-cards__viewer-choice-row';
      row.setAttribute('role', 'group');
      if (group.name) row.setAttribute('aria-label', group.name);
      group.values.forEach((choice) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'oracura-video-cards__viewer-chip';
        chip.dataset.viewerPick = '';
        chip.dataset.group = String(g);
        chip.dataset.value = choice.value;
        chip.setAttribute('aria-pressed', String(choice.on));
        if (choice.soldOut) chip.classList.add('is-soldout');
        if (group.colour) {
          const dot = document.createElement('span');
          dot.className = 'oracura-video-cards__viewer-dot';
          dot.dataset.colour = choice.value.toLowerCase().split(/[\s+/]/)[0];
          dot.setAttribute('aria-hidden', 'true');
          chip.append(dot);
        }
        chip.append(choice.text);
        row.append(chip);
      });
      box.append(row);
    });
    return box;
  }

  /**
   * On a product page the clip's Add to cart is the page's own. It closes the
   * player and submits the product form, so everything the page adds on the
   * way to the cart still applies: the quantity box, a delivery plan, line
   * item properties, the care plan, the gift offer. Adding the variant here,
   * one of it and nothing else, skipped all of that.
   *
   * The form is submitted once the player has fully closed (page unlocked,
   * focus back on the clip), so whatever the page opens next, the gift offer,
   * takes the focus and hands it back to the clip when it is done.
   */
  submitForm(button) {
    const form = document.getElementById(button.dataset.viewerSubmit);
    if (!form || button.disabled) return;
    this.afterClose = () => {
      if (!form.isConnected) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else [...form.elements].find((element) => element.type === 'submit')?.click();
    };
    this.viewer.close();
  }

  /** Builds the stage for whichever card is showing. */
  renderViewer() {
    const card = this.openable[this.index];
    if (!card) return;
    const source = card.querySelector('video');

    // A key pressed on a control in the stage replaces that control; the
    // same one in the new stage keeps the focus rather than dropping it on
    // the page behind.
    const focused = this.stage.contains(document.activeElement) ? document.activeElement : null;
    const refocus = focused?.closest('[data-viewer-sound]')
      ? '[data-viewer-sound]'
      : focused?.closest('.oracura-video-cards__viewer-add')
        ? '.oracura-video-cards__viewer-add'
        : null;

    const frame = document.createElement('div');
    frame.className = 'oracura-video-cards__viewer-frame';

    const video = document.createElement('video');
    video.className = 'oracura-video-cards__viewer-video';
    video.playsInline = true;
    video.loop = true;
    video.preload = 'auto';
    const poster = source.getAttribute('poster');
    if (poster) video.setAttribute('poster', poster);
    source.querySelectorAll('source').forEach((node) => video.appendChild(node.cloneNode(true)));
    video.muted = !this.wantsSound;

    // Tap the picture to hold it, as one would expect of a reel.
    video.addEventListener('click', () => {
      video.paused ? video.play().catch(() => {}) : video.pause();
    });

    const sound = document.createElement('button');
    sound.type = 'button';
    sound.className = 'oracura-video-cards__viewer-sound';
    sound.setAttribute('data-viewer-sound', '');

    frame.append(video, sound);

    const parts = document.createDocumentFragment();
    parts.append(frame);

    const strip = this.buildStrip(card);
    if (strip) parts.append(strip);

    this.stage.replaceChildren(parts);
    this.current = video;
    this.placeSound(frame, video, sound);

    // One card, nowhere to step to.
    this.viewer.querySelectorAll('[data-viewer-step]').forEach((button) => {
      button.hidden = this.openable.length < 2;
    });

    if (refocus) (this.stage.querySelector(refocus) || this.viewer.querySelector('[data-viewer-close]'))?.focus();

    const play = video.play();
    if (play && play.catch) {
      play.catch(() => {
        video.muted = true;
        this.markSound();
        video.play().catch(() => {});
      });
    }
    this.markSound();
  }

  /**
   * The product under the clip: the card's own strip, or the one it keeps in a
   * template when it draws a caption of its own (Watch and Buy), with the
   * model and colour to choose and Add to cart. Built again after each choice.
   */
  buildStrip(card) {
    const product =
      card.querySelector('template[data-viewer-product]')?.content.firstElementChild ||
      card.querySelector('.oracura-video-cards__product');
    if (!product) return null;
    const strip = document.createElement('div');
    strip.className = 'oracura-video-cards__viewer-strip';
    strip.append(product.cloneNode(true));
    const choices = this.choicesFor(card);
    // With nothing to choose here, at least the model and colour Add to cart
    // puts in the cart, for a product that has more than one.
    const variantTitle = card.dataset.viewerVariantTitle;
    const meta = strip.querySelector('.oracura-video-cards__product-meta');
    if (!choices.length && variantTitle && meta) {
      const variant = document.createElement('span');
      variant.className = 'oracura-video-cards__viewer-variant';
      variant.textContent = variantTitle;
      meta.append(variant);
    }
    const sale = this.saleFor(card);
    if (sale) {
      if (sale.price) this.priceStrip(strip, sale);
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'oracura-video-cards__viewer-add';
      // A product page's form does the adding (see submitForm); anywhere
      // else the site-wide add-to-cart handler adds the card's variant.
      if (sale.form) add.dataset.viewerSubmit = sale.form;
      else add.dataset.addVariant = sale.id;
      // The label lives in a span of its own because the site-wide
      // add-to-cart handler swaps that span for "Added" and back.
      const text = document.createElement('span');
      text.className = 'oracura-video-cards__viewer-add-text';
      text.textContent = this.viewer.dataset.addLabel || 'Add to cart';
      if (!sale.available) {
        // Sold out says so, rather than offering a tap that can only fail.
        add.disabled = true;
        text.textContent = this.viewer.dataset.soldoutLabel || text.textContent;
      }
      add.append(text);
      strip.append(add);
    }
    if (choices.length) strip.append(this.choiceChips(choices));
    return strip;
  }

  /** The chosen variant's prices, over the product's opening ones. */
  priceStrip(strip, sale) {
    const price = strip.querySelector('.oracura-video-cards__price');
    if (!price) return;
    price.textContent = sale.price;
    let compare = strip.querySelector('.oracura-video-cards__compare');
    if (!sale.compare) {
      compare?.remove();
      return;
    }
    if (!compare) {
      compare = document.createElement('s');
      compare.className = 'oracura-video-cards__compare';
      price.after(compare);
    }
    compare.textContent = sale.compare;
  }

  /**
   * Walks the row along on its own, one card at a time, and hands control over
   * the moment someone touches it.
   *
   * "Someone touches it" has to be judged from the scroll position rather than
   * from pointer events, because a swipe on a phone and a trackpad flick both
   * scroll the row without any click. So each automatic step records where it
   * meant to land; if the row is somewhere else when the next beat comes round,
   * a person moved it and the carousel steps aside. It comes back once they have
   * been still for a moment.
   */
  setupAutoScroll(track) {
    if (!track) return;

    const seconds = Number.parseFloat(this.getAttribute('autoplay-seconds') || '0');
    this.stepInterval = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
    if (!this.stepInterval) return;

    this.track = track;
    this.timer = null;
    this.idle = null;
    this.manual = false;
    this.expected = null;
    // How long the row waits after the last manual scroll before resuming.
    this.resumeDelay = Math.max(3000, this.stepInterval * 2);

    const handOver = () => {
      this.manual = true;
      this.stopAuto();
      window.clearTimeout(this.idle);
      this.idle = window.setTimeout(() => {
        this.manual = false;
        this.expected = null;
        this.startAuto();
      }, this.resumeDelay);
    };

    track.addEventListener(
      'scroll',
      () => {
        // A step of our own lands within a pixel or two of where it aimed;
        // anything else came from a person.
        if (this.expected !== null && Math.abs(track.scrollLeft - this.expected) <= 4) return;
        handOver();
      },
      { passive: true }
    );

    // Pointer and key use counts even when it does not move the row.
    for (const type of ['pointerdown', 'wheel', 'touchstart', 'keydown']) {
      track.addEventListener(type, handOver, { passive: true });
    }

    this.addEventListener('pointerenter', () => this.stopAuto());
    this.addEventListener('pointerleave', () => this.startAuto());
    this.addEventListener('focusin', () => this.stopAuto());
    this.addEventListener('focusout', () => this.startAuto());

    this.onVisibility = () => (document.hidden ? this.stopAuto() : this.startAuto());
    document.addEventListener('visibilitychange', this.onVisibility);

    // Rotation nobody asked for is exactly what the motion preference is about.
    // Checked at each beat rather than once at setup, so switching it back on
    // starts the row moving again without a reload.
    this.onMotion = () => (motionPaused() ? this.stopAuto() : this.startAuto());
    document.addEventListener('oracuramotionchange', this.onMotion);

    // Off-screen carousels should not tick.
    if (typeof IntersectionObserver !== 'undefined') {
      this.autoObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.inView = entry.isIntersecting;
            entry.isIntersecting ? this.startAuto() : this.stopAuto();
          }
        },
        { threshold: 0.25 }
      );
      this.autoObserver.observe(this);
    } else {
      this.inView = true;
      this.startAuto();
    }
  }

  startAuto() {
    if (this.manual || this.timer || !this.stepInterval) return;
    if (document.hidden || this.inView === false || motionPaused()) return;
    this.timer = window.setInterval(() => this.stepAuto(), this.stepInterval);
  }

  stopAuto() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  stepAuto() {
    const track = this.track;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    if (max <= 1) return;

    const card = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

    // Reverse at the ends rather than snapping back to the start, so the row
    // reads as drifting to and fro instead of restarting.
    if (track.scrollLeft >= max - 4) this.direction = -1;
    if (track.scrollLeft <= 4) this.direction = 1;

    const target = Math.max(0, Math.min(max, track.scrollLeft + step * (this.direction || 1)));
    this.expected = Math.round(target);
    track.scrollTo({ left: target, behavior: 'smooth' });

    // Snapping settles a pixel or two off the requested position; refresh the
    // expectation once it has come to rest so the next beat is not mistaken for
    // a person having moved the row.
    window.clearTimeout(this.settle);
    this.settle = window.setTimeout(() => {
      if (!this.manual) this.expected = Math.round(track.scrollLeft);
    }, 700);
  }

  setupLazyVideo() {
    const videos = this.querySelectorAll('video[data-video-lazy]');
    if (!videos.length) return;

    if (typeof IntersectionObserver === 'undefined') return;

    // How much of a card has to be on screen before it plays.
    //
    // A desktop row shows four cards side by side, so half-visible is a fair
    // test there. A phone shows one card with its neighbours peeking, and at
    // half-visible only the middle one ever qualified: the row played four
    // across on a desktop and sat on a still poster either side of one card on
    // a phone. A quarter is the mark on a narrow screen, which wakes the
    // peeking cards without ever asking a phone to decode the whole row.
    const narrow = window.matchMedia('(max-width: 749px)');
    this.narrowQuery = narrow;

    // Read from the DOM rather than from this.viewer: setupViewer runs after
    // this one, so the property is not set yet.
    const viewer = this.querySelector('[data-video-viewer]');
    this.lazyViewer = viewer;

    // Latest ratio per video rather than a visible/not decision taken in the
    // callback, so the threshold can be re-read when the phone is turned and
    // the row re-sorts itself without waiting for the next scroll.
    const ratios = new Map();

    // Battery saver refuses muted autoplay outright — iOS Low Power Mode and
    // Android's data saver both reject the play() promise, and the old catch
    // swallowed it, so a phone in either mode showed five posters and nothing
    // else for the rest of the visit. The refusal is lifted for a gesture, so
    // the next touch anywhere on the page is taken as the cue to try again.
    let armed = false;
    const armRetry = () => {
      if (armed) return;
      armed = true;
      const retry = () => {
        armed = false;
        document.removeEventListener('pointerdown', retry);
        document.removeEventListener('touchstart', retry);
        this.syncVideos?.();
      };
      document.addEventListener('pointerdown', retry, { once: true, passive: true });
      document.addEventListener('touchstart', retry, { once: true, passive: true });
    };

    const start = (video) => {
      // Safari wants the property, not only the attribute, and it wants
      // something actually fetched: preload="none" leaves readyState at 0, and
      // a play() against a source that has never been opened comes back
      // resolved on a desktop and silent on a phone.
      video.muted = true;
      if (video.readyState === 0) {
        video.preload = 'auto';
        video.load();
      }
      video.play()?.catch(armRetry);
    };

    this.syncVideos = () => {
      const floor = narrow.matches ? 0.25 : 0.5;
      // The row is wallpaper while the player is open — the clip in front of
      // it is the one being watched, and it has the sound.
      const open = viewer?.open === true;
      videos.forEach(video => {
        const wanted = !open && (ratios.get(video) || 0) >= floor && !motionPaused() && !document.hidden;
        if (wanted) start(video);
        else video.pause();
      });
    };
    document.addEventListener('oracuramotionchange', this.syncVideos);
    document.addEventListener('visibilitychange', this.syncVideos);
    narrow.addEventListener?.('change', this.syncVideos);
    // However the player is dismissed — the close button, the backdrop, Escape
    // — the dialog fires this, so the row picks itself back up.
    viewer?.addEventListener('close', this.syncVideos);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
        this.syncVideos();
      },
      // Both marks are watched so a card crossing either one reports in. The
      // ratio accounts for the page viewport (below-fold section) and for the
      // horizontal track's clipping (off-center cards) together.
      { threshold: [0, 0.25, 0.5] },
    );

    videos.forEach((video) => this.observer.observe(video));

    // Each card says whether its clip is moving, for a play mark that shows
    // while it is still (Watch and Buy draws one).
    videos.forEach((video) => {
      const card = video.closest('[data-video-card]');
      if (!card) return;
      const mark = () => card.classList.toggle('is-playing', !video.paused);
      video.addEventListener('playing', mark);
      video.addEventListener('pause', mark);
    });
  }

  /**
   * Shopify's theme editor tears a section down and rebuilds it on every edit,
   * so everything started above has to be stopped here or each edit leaves
   * another timer and another observer running against detached markup.
   */
  disconnectedCallback() {
    this.stopAuto();
    window.clearTimeout(this.idle);
    window.clearTimeout(this.settle);
    this.autoObserver?.disconnect();
    this.observer?.disconnect();
    this.querySelectorAll('video[data-video-lazy]').forEach(video => video.pause());
    if (this.syncVideos) {
      document.removeEventListener('oracuramotionchange', this.syncVideos);
      document.removeEventListener('visibilitychange', this.syncVideos);
      this.narrowQuery?.removeEventListener?.('change', this.syncVideos);
      this.lazyViewer?.removeEventListener('close', this.syncVideos);
    }
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onMotion) document.removeEventListener('oracuramotionchange', this.onMotion);

    // Taken out of the page with the player open: the dialog leaves the top
    // layer without firing close, so nothing above would run. The clip would
    // play on, out of sight and with sound, and the page under it stayed
    // locked for the rest of the visit. Let go of all of it here.
    this.viewerLife?.abort();
    if (this.viewer && this.stage) {
      this.viewerClosed();
      if (this.viewer.open) this.viewer.close();
    }
  }
}

if (!customElements.get('oracura-video-cards')) {
  customElements.define('oracura-video-cards', OracuraVideoCards);
}
